import { ConfirmDialog } from '_controls/confirm-dialog';
import { VinylColorWarning } from '_controls/vinyl-color-warning';
import * as Dto from '_models/dtos';
import { SessionData } from '_models/session-data';
import { SortService } from '_services/sort-service';
import { UrlService } from '_services/url-service';
import { DialogService } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, computedFrom, observable } from 'aurelia-framework';
import { HttpClient } from 'aurelia-http-client';
import { Router } from 'aurelia-router';
import * as Rx from 'rxjs/Rx';

@autoinject
export class ProductType {
    private _http: HttpClient;
    private _url: UrlService;
    private _subscriptions: Subscription[] = [];
    private _filterChangedEvent = new Rx.Subject();
    private _searchTextChangedEvent = new Rx.Subject();

    constructor(
        private _element: Element,
        private _eventBus: EventAggregator,
        public router: Router,
        public sd: SessionData,
        public dialogSvc: DialogService
    ) {
        this._http = new HttpClient();
        this._url = new UrlService();

        // Load product uses ddl, and then try to load selected product use from session
        this.loadProductUses().then(x => {
            var productUse = this.productUses.find(p => p.id == (this.sd.wizard.selectedProductUse && this.sd.wizard.selectedProductUse.id || null));
            this.selectedProductUse = productUse;
        });

        // Throttle the calls to applyFilters
        this._filterChangedEvent.debounceTime(100).subscribe(e => this.applyFilters());
        this._searchTextChangedEvent.debounceTime(400).subscribe(e => this.applyFilters());
    }

    readonly PRODUCT_USE_LABEL = 'PRODUCT TYPE*';
    readonly SUBSTRATE_LABEL = 'SUBSTRATE*';
    readonly SHEEN_LABEL = 'SHEEN';
    readonly OPTIONS_LABEL = 'OPTIONS';

    attached() {
        this.affixTableHeaderOnScroll();

        this._subscriptions = [
            this._eventBus.subscribe('region-changed', regionType => this._filterChangedEvent.next())
        ];

        this._filterChangedEvent.next();
    }

    detached() {
        this._subscriptions.forEach(s => s.dispose());
    }

    // Products
    productTypes: Dto.ProductType[] = [];

    // Search
    @observable searchText: string;

    // Product Use Filter
    productUses: Dto.ProductUse[] = [];
    @observable selectedProductUse: Dto.ProductUse = null;

    // Substrate Filter
    substrates: Dto.Substrate[] = [];
    @observable selectedSubstrate: Dto.Substrate = null;
    isSubstratesDisabled = true;

    // Sheen Filter
    sheens = [];

    @computedFrom('sheens')
    get nonTransparentSheens() {
        // The html template binds to the computed property "nonTransparentSheens" (no parenthesis)
        return this.sheens.filter(s => !s.isTransparency);
    }
    @computedFrom('sheens')
    get transparentSheens() {
        // The html template binds to the computed property "transparentSheens" (no parenthesis)
        return this.sheens.filter(s => s.isTransparency);
    }
    isSheensDisabled = true;
    sheensSummary: string = this.SHEEN_LABEL;

    isSheensOpen: boolean = false;
    onSheensToggled() {
        this.isSheensOpen = !this.isSheensOpen;
        if (!this.isSheensOpen) {
            this.updateSheensSummary();
            this.onSheensClosed();
        }
    }

    updateSheensSummary() {
        var summary = this.sheens
            .filter(s => s.isChecked)
            .map(s => s.name)
            .reduce((prev, next) => prev + (prev == '' ? '' : ', ') + next, '');

        this.sheensSummary = summary != '' ? summary : this.SHEEN_LABEL;
    }

    // Options filter checkboxes
    isOption_CHPS = false;
    isOption_LEEDV4 = false;
    isOption_MPI = false;
    isOption_AsthmaAllergy = false;
    isOption_VOC = false;

    optionsSummary: string = this.OPTIONS_LABEL;


    // Options dropdown state
    isOptionsOpen: boolean = false;
    onOptionsToggled() {
        this.isOptionsOpen = !this.isOptionsOpen;
        if (!this.isOptionsOpen) {
            this.updateOptionsSummary();
            this._filterChangedEvent.next();
        }
    }

    updateOptionsSummary() {
        var options = [
            { name: 'CHPS', isChecked: this.isOption_CHPS },
            { name: 'LEED V4', isChecked: this.isOption_LEEDV4 },
            { name: 'MPI', isChecked: this.isOption_MPI },
            { name: 'ASTHMA & ALLERGY', isChecked: this.isOption_AsthmaAllergy },
            { name: 'ZERO TO LOW VOC', isChecked: this.isOption_VOC }
        ];

        var summary = options
            .filter(opt => opt.isChecked)
            .map(opt => opt.name)
            .reduce((prev, next) => prev + (prev == '' ? '' : ', ') + next, '');

        this.optionsSummary = summary != '' ? summary : this.OPTIONS_LABEL;
    }

    loadProductUses() {
        var url = this._url.build('~/api/ProductUses/');
        return this._http.get(url).then(resp => {
            this.productUses = resp.content;
        });
    }

    selectedProductUseChanged(newSelection: Dto.ProductUse, oldSelection: Dto.ProductUse) {

        // Persist selection with session
        this.sd.wizard.selectedProductUse = newSelection;

        if (newSelection != null && newSelection.application != null) {
            // Load substrates for the selected product use, then try to load selected substrate from session
            this.loadSubstrates(newSelection.application).then(x => {
                var substrate = this.substrates.find(s => s.id == (this.sd.wizard.selectedSubstrate && this.sd.wizard.selectedSubstrate.id || null));
                this.selectedSubstrate = substrate;
            });
            this.isSubstratesDisabled = false;
        } else {
            this.resetSubstrate();
        }

        this._filterChangedEvent.next();
    }

    loadSubstrates(interiorExterior: string) {
        var url = this._url.build('~/api/Substrates/' + interiorExterior);
        return this._http.get(url).then(resp => {
            this.substrates = resp.content;
        });
    }

    selectedSubstrateChanged(newSelection: Dto.Substrate, oldSelection: Dto.Substrate) {

        // Persist selection with session
        this.sd.wizard.selectedSubstrate = newSelection;

        if (newSelection != null) {
            this.loadSheens(newSelection.id).then(resp => {
                this.isSheensDisabled = false;
                this.updateSheensSummary();
            });

            if (newSelection.longName == "Vinyl Siding") {
                this.dialogSvc.open({
                    viewModel: VinylColorWarning,
                    model: 'Changing the region will remove your existing product and color selections.  Are you sure you want to continue?'
                }).whenClosed(e => !e.wasCancelled);
            }
        } else {
            this.resetSheens();
        }



        this._filterChangedEvent.next();


    }

    loadSheens(substrateId: number) {
        var url = this._url.build('~/api/Sheens/BySubstrateId/' + substrateId.toString());
        return this._http.get(url).then(resp => {
            this.sheens = resp.content;
        });
    }

    onSheensClosed() {
        this._filterChangedEvent.next();
    }

    searchTextChanged(newValue, oldValue) {
        this._searchTextChangedEvent.next();
    }

    resetProductUse() {
        this.selectedProductUse = null;
        this.sd.wizard.selectedProductUse = null;
    }

    resetSubstrate() {
        this.isSubstratesDisabled = true;
        this.selectedSubstrate = null;
        this.sd.wizard.selectedSubstrate = null;
    }

    resetSheens() {
        this.isSheensDisabled = true;
        this.sheens = [];
        this.updateSheensSummary();
    }

    resetOptions() {
        this.isOption_CHPS = false;
        this.isOption_LEEDV4 = false;
        this.isOption_MPI = false;
        this.isOption_AsthmaAllergy = false;
        this.isOption_VOC = false;
        this.updateOptionsSummary();
    }

    resetAll() {
        this.resetProductUse();
        this.resetSubstrate();
        this.resetSheens();
        this.resetOptions();
        this.sd.wizard.selectedProduct = null;
    }

    productSort = new SortService('productFamily', 'asc');

    sortProducts(propertyName: string) {
        this.productSort.sort(this.productTypes, propertyName);
    }

    selectProduct(product: Dto.ProductType) {

        var selectedProductNumber = (this.sd.wizard.selectedProduct && this.sd.wizard.selectedProduct.productNumber || null);

        var isProductChange = product.productNumber != selectedProductNumber;
        var isExistingColorSelections = this.sd.wizard.selectedColors.length > 0;

        var setSelectedProduct = () =>
            this.sd.wizard.selectedProduct = isProductChange ? product : null; // change product or toggle off if same product was clicked

        if (isExistingColorSelections) {

            // Confirm if the user really wants to change products....
            this.dialogSvc.open({
                viewModel: ConfirmDialog,
                model: 'Changing to a new product line will remove your existing color selections.  Are you sure you want to continue?'
            }).whenClosed(e => {

                if (!e.wasCancelled) {

                    // Reset selected colors and change product
                    this.sd.wizard.resetSelectedColors();
                    setSelectedProduct();
                }
            });
        }
        else {
            setSelectedProduct();
        }
    }

    getCanCutImagePath(product: Dto.ProductType) {
        var relativeUrl = `~/images/CanCuts/${product.canCutImage}`;
        var url = this._url.build(relativeUrl);
        return url;
    }

    continueToColor() {
        this.sd.wizard.next();
        this.sd.save();
    }

    applyFilters() {
        if (this.sd.region == null)
            return;

        console.info("Applying Product Type Filters");
        var filter = new Dto.ProductTypeFilter();
        filter.searchText = this.searchText != null ? this.searchText : '';
        filter.productUseId = this.selectedProductUse != null ? this.selectedProductUse.id : 0;
        // TODO: Implement region selection
        filter.Region = this.sd.region.type;
        filter.chps = this.isOption_CHPS;
        filter.leedv4 = this.isOption_LEEDV4;
        filter.mpi = this.isOption_MPI;
        filter.asthmaAllergy = this.isOption_AsthmaAllergy;
        filter.voc = this.isOption_VOC;
        filter.substrateId = this.selectedSubstrate != null ? this.selectedSubstrate.id : 0;
        filter.sheenIds = this.sheens.filter(s => s.isChecked).map(s => s.id);

        // Get product types
        var url = this._url.build('~/api/ProductTypes/Filter/');
        this._http.post(url, filter).then(resp => {
            this.productTypes = resp.content;
        });
    }

    isHeaderFixed = false;

    affixTableHeaderOnScroll() {
        var thead = this._element.querySelector('#product-list-row thead');
        var theadDistTop = thead.getBoundingClientRect().top;
        window.onscroll = () => {
            this.isHeaderFixed = window.scrollY >= theadDistTop;
        };
    }
}
